import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminApi, type AdminRetailer, type PatchRetailerBody } from '../../services/adminApi';

type ActiveFilter = '' | 'true' | 'false';

const ACTIVE_FILTERS: { value: ActiveFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];
const PAGE_SIZE = 25;

function formatTimestamp(value: string | null): string {
  if (!value) return 'Never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * Admin retailer-management index page (GitHub #12 area 1).
 *
 * Lists retailers with their trust score, active / degraded flags, and last
 * scrape time, with a search box and active filter. Inline controls let an
 * admin toggle active, reset the degraded flag, and adjust the trust score.
 * Auth is enforced upstream by AdminLayout (redirects non-admins); the server
 * also re-checks admin role and audit-logs each mutation.
 *
 * Privacy / scope: operational retailer metadata only — no end-user PII. This
 * surface never triggers a live scrape.
 */
export function RetailersListPage() {
  const queryClient = useQueryClient();

  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['admin', 'retailers', { search, activeFilter, page }],
    queryFn: () =>
      adminApi.listRetailers({
        search: search || undefined,
        isActive: activeFilter === '' ? undefined : activeFilter === 'true',
        page,
        limit: PAGE_SIZE,
      }),
  });

  const mutation = useMutation({
    mutationFn: (vars: { id: string; patch: PatchRetailerBody }) =>
      adminApi.patchRetailer(vars.id, vars.patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'retailers'] });
    },
  });

  function onSearchSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  }

  function onActiveFilterChange(event: SelectChangeEvent<string>) {
    setActiveFilter(event.target.value as ActiveFilter);
    setPage(1);
  }

  function onToggleActive(retailer: AdminRetailer) {
    mutation.mutate({ id: retailer.id, patch: { isActive: !retailer.isActive } });
  }

  function onResetDegraded(retailer: AdminRetailer) {
    mutation.mutate({ id: retailer.id, patch: { isDegraded: false } });
  }

  function onTogglePromoted(retailer: AdminRetailer) {
    mutation.mutate({ id: retailer.id, patch: { isPromoted: !retailer.isPromoted } });
  }

  // ── Feed config editor (#274) ──────────────────────────────────────────────
  const [feedEditor, setFeedEditor] = useState<AdminRetailer | null>(null);
  const [feedUrlDraft, setFeedUrlDraft] = useState('');
  const [feedFormatDraft, setFeedFormatDraft] = useState<'ammoseek' | 'roundrecon'>('roundrecon');

  function openFeedEditor(retailer: AdminRetailer) {
    setFeedEditor(retailer);
    setFeedUrlDraft(retailer.feedUrl ?? '');
    setFeedFormatDraft(retailer.feedFormat ?? 'roundrecon');
  }

  function saveFeed() {
    if (!feedEditor) return;
    mutation.mutate(
      { id: feedEditor.id, patch: { feedUrl: feedUrlDraft.trim(), feedFormat: feedFormatDraft } },
      { onSuccess: () => setFeedEditor(null) }
    );
  }

  function clearFeed() {
    if (!feedEditor) return;
    // Empty url + null format clears the config → retailer reverts to scraping.
    mutation.mutate(
      { id: feedEditor.id, patch: { feedUrl: '', feedFormat: null } },
      { onSuccess: () => setFeedEditor(null) }
    );
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Retailer management
      </Typography>

      <Paper
        component="form"
        onSubmit={onSearchSubmit}
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
        elevation={0}
        variant="outlined"
      >
        <TextField
          id="retailer-search"
          label="Search by name or slug"
          size="small"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="retailer-active-filter-label">Active</InputLabel>
          <Select
            labelId="retailer-active-filter-label"
            id="retailer-active-filter"
            value={activeFilter}
            label="Active"
            onChange={onActiveFilterChange}
          >
            {ACTIVE_FILTERS.map((f) => (
              <MenuItem key={f.value || 'all'} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Paper>

      {mutation.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Update failed: {(mutation.error as Error).message}
        </Alert>
      )}

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading retailers" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load retailers: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Retailers">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Trust score</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Degraded</TableCell>
                  <TableCell>Promoted</TableCell>
                  <TableCell>Ingestion</TableCell>
                  <TableCell>Last scraped</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.retailers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        No retailers match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.retailers.map((retailer) => (
                    <TableRow key={retailer.id} hover>
                      <TableCell>{retailer.name}</TableCell>
                      <TableCell>
                        {retailer.trustScore === null ? '—' : retailer.trustScore.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={retailer.isActive ? 'Active' : 'Inactive'}
                          color={retailer.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={retailer.isDegraded ? 'Degraded' : 'OK'}
                          color={retailer.isDegraded ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={retailer.isPromoted ? 'Promoted' : 'No'}
                          color={retailer.isPromoted ? 'info' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={retailer.feedUrl ? `Feed: ${retailer.feedFormat}` : 'Scrape'}
                          color={retailer.feedUrl ? 'success' : 'default'}
                          size="small"
                          variant={retailer.feedUrl ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{formatTimestamp(retailer.lastScrapedAt)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={mutation.isPending}
                            onClick={() => openFeedEditor(retailer)}
                            aria-label={`Edit feed for ${retailer.name}`}
                          >
                            Feed
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color={retailer.isActive ? 'warning' : 'success'}
                            disabled={mutation.isPending}
                            onClick={() => onToggleActive(retailer)}
                            aria-label={`${
                              retailer.isActive ? 'Deactivate' : 'Activate'
                            } ${retailer.name}`}
                          >
                            {retailer.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                          {retailer.isDegraded && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={mutation.isPending}
                              onClick={() => onResetDegraded(retailer)}
                              aria-label={`Reset degraded flag for ${retailer.name}`}
                            >
                              Reset degraded
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={mutation.isPending}
                            onClick={() => onTogglePromoted(retailer)}
                            aria-label={`${
                              retailer.isPromoted ? 'Unpromote' : 'Promote'
                            } ${retailer.name}`}
                          >
                            {retailer.isPromoted ? 'Unpromote' : 'Promote'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Retailers pagination"
            />
          </Box>
        </>
      )}

      <Dialog
        open={feedEditor !== null}
        onClose={() => setFeedEditor(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Feed ingestion — {feedEditor?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            When a feed URL is set, this retailer is ingested from its feed and is{' '}
            <strong>not scraped</strong>. Clear the URL to revert to scraping.
          </Typography>
          <TextField
            label="Feed URL"
            type="url"
            fullWidth
            value={feedUrlDraft}
            onChange={(e) => setFeedUrlDraft(e.target.value)}
            placeholder="https://retailer.example.com/roundrecon-feed.json"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel id="feed-format-label">Feed format</InputLabel>
            <Select
              labelId="feed-format-label"
              label="Feed format"
              value={feedFormatDraft}
              onChange={(e) => setFeedFormatDraft(e.target.value as 'ammoseek' | 'roundrecon')}
            >
              <MenuItem value="roundrecon">Round Recon (proprietary JSON)</MenuItem>
              <MenuItem value="ammoseek">AmmoSeek (XML)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFeed} color="warning" disabled={mutation.isPending}>
            Clear (use scraping)
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setFeedEditor(null)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={saveFeed}
            variant="contained"
            disabled={mutation.isPending || feedUrlDraft.trim() === ''}
          >
            Save feed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RetailersListPage;
